import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Landmark, Globe2, Send, ShieldCheck, Trash2, Save } from "lucide-react";

const METHOD_TABS = [
  { key: "uk_bank", label: "UK bank", icon: Landmark, blurb: "Fastest for UK-based creators. 1–2 business days." },
  { key: "iban",    label: "IBAN",    icon: Globe2,   blurb: "For SEPA / international bank accounts. 3–5 business days." },
  { key: "paypal",  label: "PayPal",  icon: Send,     blurb: "Instant to your PayPal balance. No Nexus commission." },
];

export default function PayoutMethod() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("uk_bank");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({
    account_holder: "",
    sort_code: "",
    account_number: "",
    iban: "",
    swift_bic: "",
    paypal_email: "",
    country: "GB",
  });

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    if (user.role !== "creator") { navigate("/wallet"); return; }
    api.get("/wallet/payout-method").then((r) => {
      const pm = r.data.payout_method;
      if (pm) {
        setExisting(pm);
        setTab(pm.type);
        setForm((f) => ({ ...f, account_holder: pm.account_holder || "" }));
      }
    }).catch(() => {});
  }, [user, loading, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { type: tab, account_holder: form.account_holder.trim(), country: form.country };
      if (tab === "uk_bank") {
        payload.sort_code = form.sort_code;
        payload.account_number = form.account_number;
      } else if (tab === "iban") {
        payload.iban = form.iban;
        if (form.swift_bic) payload.swift_bic = form.swift_bic;
      } else if (tab === "paypal") {
        payload.paypal_email = form.paypal_email;
      }
      const { data } = await api.put("/wallet/payout-method", payload);
      setExisting(data.payout_method);
      toast.success("Payout method saved — you can withdraw whenever your balance clears.");
      // Blank sensitive inputs after save (they're encrypted / snapshotted server-side)
      setForm((f) => ({ ...f, sort_code: "", account_number: "", iban: "", swift_bic: "", paypal_email: "" }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your payout method? You won't be able to withdraw until you add a new one.")) return;
    setDeleting(true);
    try {
      await api.delete("/wallet/payout-method");
      setExisting(null);
      setForm({ account_holder: "", sort_code: "", account_number: "", iban: "", swift_bic: "", paypal_email: "", country: "GB" });
      toast.success("Payout method removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const activeTabDef = METHOD_TABS.find((t) => t.key === tab);

  const canSubmit = form.account_holder.trim().length >= 2 && (
    (tab === "uk_bank" && form.sort_code.replace(/\D/g, "").length === 6 && form.account_number.replace(/\D/g, "").length === 8) ||
    (tab === "iban" && form.iban.replace(/\s/g, "").length >= 15) ||
    (tab === "paypal" && /^\S+@\S+\.\S+$/.test(form.paypal_email))
  );

  return (
    <div className="min-h-screen grain">
      <SEO title="Payout method · Nexus" description="Set how Nexus pays you — UK bank, IBAN or PayPal." path="/creator/payout-method" />
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/wallet" label="Back to wallet" />
      </div>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pb-10 pt-4 space-y-8">
        {/* Header */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Payout gateway</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
            How should we <span className="nexus-gradient-text">pay you</span>?
          </h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-xl">
            Nexus reviews every withdrawal and pays you directly to the method below. Your details are stored securely and only revealed to the payout team when a withdrawal is queued.
          </p>
        </div>

        {/* Existing method banner */}
        {existing && (
          <div className="glass rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="existing-payout-method">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-emerald-300" />
              </div>
              <div>
                <div className="text-sm font-semibold">
                  Current method: <span className="uppercase text-emerald-300">{existing.type.replace("_", " ")}</span>
                </div>
                <div className="text-xs text-zinc-400 mt-0.5">
                  {existing.type === "uk_bank"  && `${existing.account_holder} · ${existing.sort_code_masked} · ••••${existing.account_last4}`}
                  {existing.type === "iban"     && `${existing.account_holder} · ${existing.iban_masked}`}
                  {existing.type === "paypal"   && `${existing.paypal_email_masked}`}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">Update the fields below and save to replace.</div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={remove}
              disabled={deleting}
              className="bg-red-500/5 border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200 rounded-full"
              data-testid="remove-payout-method-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> {deleting ? "Removing…" : "Remove"}
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-2" data-testid="payout-method-tabs">
          {METHOD_TABS.map((t) => {
            const Icon = t.icon;
            const on = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`payout-tab-${t.key}`}
                className={`p-4 rounded-2xl border transition-all text-left ${on ? "border-nexus-pink/60 bg-nexus-pink/10" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
              >
                <Icon className={`h-5 w-5 mb-2 ${on ? "text-nexus-pink" : "text-zinc-400"}`} />
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-[11px] text-zinc-500 mt-1 leading-snug">{t.blurb}</div>
              </button>
            );
          })}
        </div>

        {/* Form */}
        <div className="glass rounded-2xl p-6 space-y-5" data-testid="payout-form">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Account holder name</Label>
            <Input
              value={form.account_holder}
              onChange={(e) => setForm((f) => ({ ...f, account_holder: e.target.value.slice(0, 120) }))}
              placeholder="Must match the name on the account"
              className="mt-1.5 bg-white/[0.02] border-white/10 text-white"
              data-testid="account-holder-input"
            />
          </div>

          {tab === "uk_bank" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Sort code</Label>
                <Input
                  value={form.sort_code}
                  onChange={(e) => setForm((f) => ({ ...f, sort_code: e.target.value.slice(0, 8) }))}
                  placeholder="12-34-56"
                  inputMode="numeric"
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white font-mono"
                  data-testid="sort-code-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Account number</Label>
                <Input
                  value={form.account_number}
                  onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value.replace(/\D/g, "").slice(0, 8) }))}
                  placeholder="12345678"
                  inputMode="numeric"
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white font-mono"
                  data-testid="account-number-input"
                />
              </div>
            </div>
          )}

          {tab === "iban" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">IBAN</Label>
                <Input
                  value={form.iban}
                  onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value.toUpperCase().slice(0, 40) }))}
                  placeholder="GB29 NWBK 6016 1331 9268 19"
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white font-mono"
                  data-testid="iban-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">SWIFT / BIC <span className="text-zinc-600 normal-case tracking-normal">· optional</span></Label>
                <Input
                  value={form.swift_bic}
                  onChange={(e) => setForm((f) => ({ ...f, swift_bic: e.target.value.toUpperCase().slice(0, 11) }))}
                  placeholder="NWBKGB2L"
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white font-mono"
                  data-testid="swift-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Country</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value.toUpperCase().slice(0, 2) }))}
                  placeholder="GB"
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white font-mono"
                  data-testid="country-input"
                />
              </div>
            </div>
          )}

          {tab === "paypal" && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">PayPal email</Label>
              <Input
                type="email"
                value={form.paypal_email}
                onChange={(e) => setForm((f) => ({ ...f, paypal_email: e.target.value.trim().toLowerCase() }))}
                placeholder="you@paypal.example"
                className="mt-1.5 bg-white/[0.02] border-white/10 text-white"
                data-testid="paypal-email-input"
              />
              <p className="text-[11px] text-zinc-500 mt-2">We&apos;ll send funds via PayPal Friends & Family so no additional fees are deducted by PayPal.</p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
            <div className="text-xs text-zinc-500 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" />
              Stored encrypted · never shown to brands · used only for payouts
            </div>
            <Button
              onClick={save}
              disabled={saving || !canSubmit}
              className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 h-11 px-6"
              data-testid="save-payout-method-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving…" : (existing ? `Update ${activeTabDef.label}` : `Save ${activeTabDef.label}`)}
            </Button>
          </div>
        </div>

        {/* Fee explainer */}
        <div className="text-xs text-zinc-500 space-y-1 text-center">
          <div>Minimum withdrawal: <span className="text-zinc-300 font-semibold">£25</span> · Hold period after brand approval: <span className="text-zinc-300 font-semibold">3 days</span></div>
          <div>Creators keep <span className="text-zinc-300 font-semibold">100%</span> of every released payout — Nexus charges its platform fee to the brand.</div>
        </div>
      </div>
    </div>
  );
}
